import React from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import { getPersonnelSuivisRequest, IPersonnelSuiviGeneralModel } from "../../core/_requests"

const REACT_QUERY_KEY_GET_PERSONNEL_SUIVIS = "REACT_QUERY_KEY_GET_PERSONNEL_SUIVIS"

// Icône par type de suivi.
const iconForType = (type: string): string => {
     switch (type) {
          case "TYPE_ENVOI_DOCUMENTS_EMBAUCHE":
               return "fa-paper-plane text-success"
          case "TYPE_CHANGEMENT_CODE_ANALYTIQUE":
               return "fa-hashtag text-primary"
          case "TYPE_CHANGEMENT_NUMERO_IMMAT_CEGEDIM":
               return "fa-id-badge text-info"
          case "TYPE_MODIFICATION_INFOS_PERSONNELLES":
               return "fa-user-pen text-warning"
          default:
               return "fa-circle-info text-muted"
     }
}

// Journal des actions (PersonnelSuiviGeneral) — corps réutilisable (onglet Suivi + onglet Paramètres du compte).
const SuiviList = ({ personnelId }: { personnelId?: number }) => {
     const suivisQuery = useQuery<IPersonnelSuiviGeneralModel[], AxiosError>(
          [REACT_QUERY_KEY_GET_PERSONNEL_SUIVIS, personnelId],
          async () => (await getPersonnelSuivisRequest(personnelId as number)).data,
          { enabled: !!personnelId }
     )

     if (suivisQuery.isFetching) {
          return (
               <div className="text-center p-8">
                    <MySimpleSpinner size={"lg"} />
               </div>
          )
     }

     if (suivisQuery.isError) {
          return (
               <MyAlert type={"danger"} classNames={"mb-0"}>
                    <>
                         <span className="me-2">Impossible de charger le journal des actions.</span>
                         <button className="btn btn-sm btn-danger" onClick={() => suivisQuery.refetch()}>
                              Recharger
                         </button>
                    </>
               </MyAlert>
          )
     }

     const suivis = suivisQuery.data ?? []

     if (suivis.length === 0) {
          return <div className="text-muted fst-italic py-6 text-center">Aucune action enregistrée pour ce collaborateur.</div>
     }

     return (
          <div className="timeline timeline-border-dashed">
               {suivis.map(s => (
                    <div className="timeline-item" key={s.id}>
                         <div className="timeline-icon">
                              <i className={`fas ${iconForType(s.type)} fs-4`} />
                         </div>
                         <div className="timeline-content mb-8">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                   <span className="fw-bold text-gray-900">{s.typeLabel ?? s.type}</span>
                              </div>
                              <div className="text-muted fs-7">
                                   {s.date ? moment(s.date).format("dddd D MMMM YYYY à H[h]mm") : ""}
                                   {s.auteur ? ` — par ${s.auteur}` : ""}
                              </div>
                              {s.commentaire && (
                                   <div className="fs-7 text-gray-700 mt-2" style={{ whiteSpace: "pre-line" }}>
                                        {s.commentaire}
                                   </div>
                              )}
                         </div>
                    </div>
               ))}
          </div>
     )
}

export default SuiviList
